import {createFeatureSelector, createSelector} from '@ngrx/store';
import {SettingsState} from './settings.reducer';
import {IStore} from '../../../shared/shared-models/store/store';
import {selectColleagues} from '../../core/store-shared/shared.selectors';
import {IUser} from 'src/app/features/user-access/models/user/user';
import {IColleagues, IColleagueUser} from '../../user-access/models';
import {selectUser} from '../../user-access/user-store/user.selectors';
import {selectSelectedStore} from '../../user-access/user-stores-store/user-store.selectors';
import {IOnHandState} from '../models';
import {
  _DEFAULT_NGP_REPORT_PREVIEW_COLS,
  _DEFAULT_OPEN_ORDER_SETTINGS,
  _DEFAULT_STOCK_MANAGER_PREVIEW_COLS,
  IDisableStockItemRule,
  IEmailSettings,
  IEnableStockItemRule,
  IOpenOrderSettings,
  ITableNavSettings,
  LocaleDateFormatEnum,
  TLineColour,
  TLocaleDateFormat,
} from '../../settings-new/models';

const selectSettingsState = createFeatureSelector<SettingsState>('settings');

// Select NGP Report User Settings
export const selectNGPReportColPreviewSettings = createSelector(
  selectSettingsState,
  (state: SettingsState) =>
    state.userSettings?.ngpReportColPreviewSettings ?? _DEFAULT_NGP_REPORT_PREVIEW_COLS,
);

// Select Stock Manager Column Preview Settings
/**
 * TODO TECHNICAL DEBT - export const selectStockManagerColPreviewSettings = createSelector()
 * We need to make a default setting so as to not return an undefined here
 *
 */
export const selectStockManagerColPreviewSettings = createSelector(
  selectSettingsState,
  selectSelectedStore,
  (state: SettingsState, store: IStore): Record<string, string> | undefined => {
    const settings = state?.userSettings?.stockManagerColPreviewSettings;
    return settings ?? _DEFAULT_STOCK_MANAGER_PREVIEW_COLS;
  },
);


// Select Disabled Rules
export const selectDisabledRulesDoc = createSelector(
  selectSettingsState,
  selectSelectedStore,
  (state: SettingsState, selectedStore: IStore) =>
    state.storeSettings[selectedStore.storeId]?.disabledRules,
);

export const selectEnabledRulesDoc = createSelector(
  selectSettingsState,
  selectSelectedStore,
  (state: SettingsState, selectedStore: IStore): IEnableStockItemRule =>
    state.storeSettings[selectedStore.storeId]?.enabledRules,
);

// Select Line Colour
export const selectIsLineColoursLoading = createSelector(
  selectSettingsState,
  (state: SettingsState) => state.isLineColoursLoading,
);


export const selectLineColour = createSelector(
  selectSettingsState,
  selectSelectedStore,
  (state: SettingsState, selectedStore: IStore) =>
    state.storeSettings[selectedStore.storeId]?.lineColour,
);

// Select Table Navigation Settings
export const selectNavSettings = createSelector(
  selectSettingsState,
  (state: SettingsState) => state.userSettings.tableNavigationSettings,
);

export const selectFitToGrid = createSelector(
  selectSettingsState,
  (state: SettingsState) => state.userSettings.fitToGrid,
);

export const selectPriceThresholds = createSelector(
  selectSettingsState,
  selectSelectedStore,
  (state: SettingsState, selectedStore: IStore) =>
    state.storeSettings[selectedStore.storeId]?.priceThresholds,
);

export const selectPriceBands = createSelector(
  selectSettingsState,
  selectSelectedStore,
  (state: SettingsState, selectedStore: IStore) =>
    state.storeSettings[selectedStore.storeId]?.priceBands,
);

export const selectAllUsersEmailSettingForCurrentSelectedStore = createSelector(
  selectSettingsState,
  selectSelectedStore,
  (state: SettingsState, store: IStore) => state.usersEmailSettings[store.storeId],
);

export const selectIsUserEmailsLoading = createSelector(
  selectSettingsState,
  (state: SettingsState) => state.isUserEmailSettingsLoading,
);

export const selectIsSelectedUserEmailSettingsLoading = createSelector(
  selectSettingsState,
  (state: SettingsState) => state.isSelectedUserEmailSettingsLoading,
);

export const selectSelectedUserEmailSettings = createSelector(
  selectSettingsState,
  (state: SettingsState) => state.selectedUserEmailSettings,
);

export const selectUserSettingsIsSubmitting = createSelector(
  selectSettingsState,
  (state: SettingsState) => state.userSettings.isSubmitting,
);

export const selectOpenOrderSettings = createSelector(
  selectSettingsState,
  selectSelectedStore,
  (state: SettingsState, store: IStore): Readonly<IOpenOrderSettings> => state.storeSettings?.[store.storeId]?.openOrderSettings || _DEFAULT_OPEN_ORDER_SETTINGS,
);

export const selectSupplierOverRide = createSelector(
  selectSettingsState,
  selectSelectedStore,
  (state: SettingsState, store: IStore): boolean => state.storeSettings?.[store.storeId]?.overrides?.orderBlankSupplierCode ?? false,
);

export const selectShelfTalkerOverRide = createSelector(
  selectSettingsState,
  selectSelectedStore,
  (state: SettingsState, store: IStore): boolean => state.storeSettings?.[store.storeId]?.overrides?.shelfTalkersEnabled ?? true,
);

export const selectAllUserSettings = createSelector(
  selectNavSettings,
  selectFitToGrid,
  selectNGPReportColPreviewSettings,
  selectStockManagerColPreviewSettings,
  selectOpenOrderSettings,
  (
    navSettings: ITableNavSettings,
    fitToGrid: boolean,
    ngpSettings: Record<number, string>,
    stockSettings: Record<string, string>,
    openOrderSettings: IOpenOrderSettings,
  ) => ({
    navSettings,
    fitToGrid,
    ngpSettings,
    stockSettings,
    openOrderSettings,
  }),
);

export const selectItemDisablingSettings = createSelector(
  selectDisabledRulesDoc,
  selectLineColour,
  (rulesDoc: IDisableStockItemRule & IOnHandState, lineColours: TLineColour) => ({
    rulesDoc,
    lineColours,
  }),
);
export const selectItemEnablingSettings = createSelector(
  selectEnabledRulesDoc,
  selectLineColour,
  (rulesDoc: IEnableStockItemRule, lineColours: TLineColour) => ({
    rulesDoc,
    lineColours,
  }),
);

export const selectUserStoreSettingsIsSubmitting = createSelector(
  selectSettingsState,
  (state: SettingsState) => state.isSubmittingStoreSettings,
);

export const selectUserEmailSettingsIsSubmitting = createSelector(
  selectSettingsState,
  (state: SettingsState) => state.isSubmittingEmailSettings,
);

export const selectAddStockItemSettings = createSelector(
  selectSettingsState,
  selectSelectedStore,
  (state: SettingsState, store: IStore): {
    prefix: string;
    suffix: string;
    digits: number
  } => state.storeSettings?.[store.storeId]?.addStockItemRules,
);

export const selectEmailSettingsView = createSelector(
  selectUser,
  selectColleagues,
  selectSelectedStore,
  selectAllUsersEmailSettingForCurrentSelectedStore,
  selectUserEmailSettingsIsSubmitting,
  (
    currentUser: IUser,
    colleagues: IColleagues,
    selectedStore: IStore,
    emailSettings: Record<string, IEmailSettings>,
    isSubmitting: boolean,
  ) => {
    const eligibleUsers = [
      currentUser,
      ...Object.values(colleagues)
        .filter((colleague: IColleagueUser) =>
          colleague.userData !== null &&
          emailSettings &&
          colleague.userData.id in emailSettings,
        ).map((colleague: IColleagueUser) => colleague.userData),
    ];
    return {
      colleagues: eligibleUsers,
      emailSettings,
      selectedStore,
      isSubmitting,
    };
  },
);
